import { useState, useEffect, useRef, useCallback } from 'react'
import { apiUrl } from '../constants/api'

/**
 * SSE hook — connects to signal-engine's Server-Sent Events stream.
 * Returns live signals array, connection status, and reconnect helper.
 *
 * @param {string} url  SSE endpoint, e.g. '/api/signals/stream'
 * @param {object} opts { maxItems, delayMs }
 */
export function useSSE(url, { maxItems = 200, delayMs = 0 } = {}) {
  const [signals, setSignals] = useState([])
  const [status, setStatus] = useState('connecting') // connecting | open | error
  const sourceRef = useRef(null)
  const retryRef = useRef(0)

  const connect = useCallback(() => {
    if (sourceRef.current) sourceRef.current.close()

    const es = new EventSource(apiUrl(url))
    sourceRef.current = es
    setStatus('connecting')

    es.onopen = () => {
      setStatus('open')
      retryRef.current = 0
    }

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        // Skip system messages (CONNECTED, HEARTBEAT, etc.) — only keep real signals
        const SIGNAL_TYPES = new Set([
          'NEW_TOKEN', 'RUG_ALERT', 'WHALE_ACCUMULATION',
          'HIGH_CONVICTION', 'BUNDLE_DETECTED',
        ])
        if (!SIGNAL_TYPES.has(data.type)) return

        const add = () =>
          setSignals((prev) => {
            const next = [data, ...prev]
            return next.length > maxItems ? next.slice(0, maxItems) : next
          })

        if (delayMs > 0) {
          setTimeout(add, delayMs)
        } else {
          add()
        }
      } catch {
        // skip malformed events
      }
    }

    es.onerror = () => {
      setStatus('error')
      es.close()
      // exponential backoff (max 30s)
      const wait = Math.min(1000 * 2 ** retryRef.current, 30000)
      retryRef.current++
      setTimeout(connect, wait)
    }
  }, [url, maxItems, delayMs])

  useEffect(() => {
    connect()
    return () => sourceRef.current?.close()
  }, [connect])

  const clear = useCallback(() => setSignals([]), [])

  return { signals, status, clear, reconnect: connect }
}
